import React, { Fragment, useEffect, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { Check, ChevronUp, ChevronRight, LoaderCircle, BrainCircuit, Lock, AlertCircle, Gauge } from "lucide-react";
import { useModel } from "../../context/ModelContext";
import { useSession } from "../../context/SessionContext";
import { useInternet } from "../../context/InternetContext";
import { useHat } from "../../context/HatContext";
import { useWorkflow } from "../../context/WorkflowContext";
import { isVoiceModel } from "../../utils/NovaVoiceManager";
import AnthropicIcon from "../../icons/anthropic";
import MetaIcon from "../../icons/meta";
import { User } from "../../types";

interface ModelCategory {
  id: string;
  name: string;
  models: any[];
  description: string;
}

// Effort levels as the API names them, in menu order, with the labels users see.
// "off" is offered only on models that run without thinking unless asked (Opus 4.7/4.8).
const EFFORT_LABELS: Record<string, string> = {
  off: "Off",
  low: "Low",
  medium: "Medium",
  high: "High",
  xhigh: "Extra",
  max: "Max",
};

// Thinking depth for the selected model. Hidden for models that take no effort setting.
// Stays usable mid-conversation, where the model itself is locked.
const EffortMenu: React.FC = () => {
  const { selectedModel, effort, setEffort } = useModel();
  const { selectedWorkflow } = useWorkflow();
  const levels = (selectedModel?.supported_efforts || []).filter(level => EFFORT_LABELS[level]);
  // Workflows run at their model's default effort, so the menu would do nothing there.
  if (!effort || levels.length === 0 || selectedWorkflow) return null;

  // Same box as the model selector (width, padding, icon slot, chevron) so the two match exactly.
  return (
    <div className="w-48 sm:w-48 lg:w-60">
      <Listbox value={effort} onChange={setEffort}>
        <div className="relative mt-1">
          <Listbox.Button
            title="Effort"
            className="relative w-full rounded-lg bg-surface-overlay shadow-sm hover:bg-surface-2 hover:shadow py-2 pl-3 pr-10 text-left transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface sm:text-sm"
          >
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <Gauge className="flex-shrink-0 w-4 h-4 text-subtle" aria-hidden="true" />
              <span className="block truncate text-xs">
                <span className="text-subtle">Effort</span>{" "}
                <span className="font-medium text-primary">{EFFORT_LABELS[effort]}</span>
              </span>
            </div>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUp className="h-5 w-5 text-subtle" aria-hidden="true" />
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute right-0 mt-1 w-full min-w-[16rem] overflow-auto rounded-md bg-surface-overlay text-primary py-1 ring-1 ring-border-default focus:outline-none text-xs shadow-lg z-10">
              <div className="px-4 py-2 text-subtle">
                Higher effort means more thorough responses, but takes longer and costs more.
              </div>
              {levels.map(level => (
                <Listbox.Option
                  key={level}
                  value={level}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 pl-4 pr-10 ${active ? "bg-surface-2" : ""}`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span className="flex items-center gap-2 font-medium">
                        {EFFORT_LABELS[level]}
                        {level === selectedModel?.default_effort && (
                          <span className="rounded border border-border-default px-1.5 text-[10px] font-normal text-subtle">
                            Recommended
                          </span>
                        )}
                        {level === "max" && (
                          <span className="flex items-center gap-1 rounded border border-amber-300 px-1.5 text-[10px] font-normal text-amber-600">
                            <AlertCircle className="h-3 w-3" aria-hidden="true" />
                            Much higher usage
                          </span>
                        )}
                      </span>
                      {selected && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary">
                          <Check className="h-4 w-4" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};

const ErrorAlert: React.FC<{ message: string }> = ({ message }) => (
  <div className="w-48 sm:w-48 lg:w-60 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
    {message}
  </div>
);

export default function ModelListBox() {
  const {
    models,
    selectedModel,
    setSelectedModel,
    loading,
    error,
  } = useModel();

  const { sessionId, sessions } = useSession();
  const { internetEnabled } = useInternet();
  const { selectedHat } = useHat();
  const { selectedWorkflow } = useWorkflow();

  const [selectedCategory, setSelectedCategory] = useState<ModelCategory | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [isVoiceModeActive, setIsVoiceModeActive] = useState(false);

  const currentSession = sessions.find(session => session.id === sessionId);
  const isModelSelectionDisabled = Boolean(
    sessionId
    || (selectedHat && Boolean(selectedHat.model_id && selectedHat.model_id.trim()))
    || (selectedWorkflow && Boolean(selectedWorkflow.model_id && selectedWorkflow.model_id.trim()))
  );
  const availableModels = models.filter(model => model.is_enabled);

  // function to check if a model should be disabled
  const isModelDisabled = (model: any) => {
    const vaSupport = model.tool_call_support || false;

    // If user has selected a VA, they need VA support
    if (selectedHat && !vaSupport) {
      return true;
    }

    return false;
  };

  // Detect when Nova Sonic is selected
  useEffect(() => {
    const isNovaSonicSelected = isVoiceModel(selectedModel);
    setIsVoiceModeActive(isNovaSonicSelected);
  }, [selectedModel]);

  // Categorize models based on their support features
  // const categorizeModels = (): ModelCategory[] => {
  //   const categories: ModelCategory[] = [
  //     {
  //       id: 'full_support',
  //       name: 'Optimized for VA’s and Connectors',
  //       description: 'Models with both voice assistant and file upload capabilities',
  //       models: []
  //     },
  //     {
  //       id: 'partial_support',
  //       name: 'Partial or Not optimal for VA’s and Connectors',
  //       description: 'Models with limited or no VA/file support',
  //       models: []
  //     }
  //   ];

  //   availableModels.forEach(model => {
  //     const fileSupport = model.file_upload_support || false;
  //     const vaSupport = model.tool_call_support || false;

  //     if (fileSupport && vaSupport) {
  //       // Both features supported
  //       categories[0].models.push(model);
  //     } else {
  //       // Only file support, only VA support, or no support at all
  //       categories[1].models.push(model);
  //     }
  //   });

  //   // Return only categories that have models
  //   return categories.filter(category => category.models.length > 0);
  // };

  // const modelCategories = categorizeModels();

  // Find which category the currently selected model belongs to
  // const findCategoryForModel = (model: any): ModelCategory | null => {
  //   return modelCategories.find(category => 
  //     category.models.some(m => m.foundation_model_id === model?.foundation_model_id)
  //   ) || null;
  // };

  useEffect(() => {
    // Initialize first available model if none selected
    if (!selectedModel && availableModels.length > 0) {
      setSelectedModel(availableModels[0]);
    }
  }, [selectedModel, availableModels]);

   useEffect(() => {
    if (selectedHat && selectedHat.model_id) {
      // Find the model in the available models list
      const vaModel = models.find(
        (model) => model.foundation_model_id === selectedHat.model_id
      );
      // If found and enabled, select it automatically
      if (vaModel && vaModel.is_enabled) {
        console.log("🎯 Auto-selecting VA's model:", vaModel.display_name);
        setSelectedModel(vaModel);
      } else if (selectedHat.model_id && !vaModel) {
        // VA has a model but it's not available/enabled
        console.warn("⚠️ VA's assigned model not available:", selectedHat.model_id);
      }
    }
  }, [selectedHat, models, setSelectedModel]);

  // When a workflow with an assigned model is selected, pin the chat dropdown
  // to that model (same UX as selectedHat-with-model above).
  useEffect(() => {
    if (selectedWorkflow && selectedWorkflow.model_id && selectedWorkflow.model_id.trim()) {
      const wfModel = models.find(
        (model) => model.foundation_model_id === selectedWorkflow.model_id
      );
      if (wfModel && wfModel.is_enabled) {
        console.log("🎯 Auto-selecting Workflow's model:", wfModel.display_name);
        setSelectedModel(wfModel);
      } else if (!wfModel) {
        console.warn("⚠️ Workflow's assigned model not available:", selectedWorkflow.model_id);
      }
    }
  }, [selectedWorkflow, models, setSelectedModel]);

  // useEffect(() => {
  //   // Update category when selected model changes
  //   if (selectedModel) {
  //     const category = findCategoryForModel(selectedModel);
  //     setSelectedCategory(category);
  //   }
  // }, [selectedModel]);

  const getProviderIcon = (provider: string) => {
    switch (provider.toLowerCase()) {
      case "anthropic":
        return <AnthropicIcon className="flex-shrink-0 w-4 h-4" />;
      case "meta":
        return <MetaIcon className="flex-shrink-0 w-4 h-4" />;
      case "amazon":
        return <BrainCircuit className="flex-shrink-0 w-4 h-4" />;
      default:
        return <BrainCircuit className="flex-shrink-0 w-4 h-4" />;
    }
  };

  // Auto-open voice mode when Nova Sonic is manually selected
  useEffect(() => {
    if (isVoiceModel(selectedModel)) {
      // Small delay to ensure UI has updated
      const timer = setTimeout(() => {
        const event = new CustomEvent('openVoiceMode');
        window.dispatchEvent(event);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [selectedModel]);

  // const toggleCategory = (categoryId: string) => {
  //   const newExpanded = new Set(expandedCategories);
  //   if (newExpanded.has(categoryId)) {
  //     newExpanded.delete(categoryId);
  //   } else {
  //     newExpanded.add(categoryId);
  //   }
  //   setExpandedCategories(newExpanded);
  // };

  // Extract foundation model name from foundation_model_id and clean it up
  const getFoundationModelName = (foundationModelId: string) => {
    return foundationModelId
      .replace(/^(anthropic\.|meta-llama\/|openai\/|google\/)/i, '') // Remove provider prefixes
      .replace(/-/g, ' ') // Replace hyphens with spaces
      .replace(/\d{8}/g, '') // Remove 8-digit dates (like 20250219)
      .replace(/v\d+(\.\d+)*/gi, '') // Remove version numbers (v1, v2.0, etc.)
      .replace(/:\d+$/g, '') // Remove trailing colon numbers (:0, :1, etc.)
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim() // Remove leading/trailing spaces
      .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
  };

  if (loading) {
    return (
      <div className="w-48 sm:w-48 lg:w-60 flex items-center justify-center p-2">
        <LoaderCircle className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  if (availableModels.length === 0) {
    return (
      <div className="w-48 sm:w-48 lg:w-60 text-sm text-subtle p-2 bg-surface-2 rounded-lg">
        No models available
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-48 sm:w-48 lg:w-60">
        <Listbox
          value={selectedModel}
          onChange={(model) => {
            // Added validation check
            if (isModelDisabled(model)) {
              return; // Don't allow selection
            }

            // Preserve original direct model selection behavior
            setSelectedModel(model);
            // // Update category for UI purposes
            // const category = findCategoryForModel(model);
            // setSelectedCategory(category);
          }}
          disabled={isModelSelectionDisabled}
        >
          <div className="relative mt-1">
            <Listbox.Button className={`relative w-full rounded-lg bg-surface-overlay shadow-sm hover:bg-surface-2 hover:shadow py-2 pl-3 pr-10 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface sm:text-sm ${isModelSelectionDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  {selectedModel && getProviderIcon(selectedModel.provider)}
                  <div className="flex-1 min-w-0">
                    <span className="block truncate font-medium text-primary text-xs">
                      {selectedModel ? (selectedModel.name || getFoundationModelName(selectedModel.foundation_model_id)) : "Select a model"}
                    </span>
                  </div>
                </div>
                {isModelSelectionDisabled && (
                  <Lock className="h-4 w-4 text-subtle mr-2 flex-shrink-0" />
                )}
              </div>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUp className="h-5 w-5 text-subtle" aria-hidden="true" />
              </span>
            </Listbox.Button>

            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute mt-1 max-h-80 w-60 sm:w-60 lg:w-80 -left-12 overflow-auto rounded-md bg-surface-overlay text-primary py-1 ring-1 ring-border-default focus:outline-none sm:text-xs text-xs shadow-lg z-10">
                {availableModels.map((model) => {
                  const disabled = isModelDisabled(model);
                  return (
                    <Listbox.Option
                      key={model.foundation_model_id}
                      value={model}
                      disabled={disabled}
                      className={({ active }) => {
                        if (disabled) {
                          return "relative cursor-not-allowed select-none py-3 pl-10 pr-4 text-subtle bg-surface-2";
                        }
                        return `relative cursor-pointer select-none py-3 pl-10 pr-4 text-primary ${active ? "bg-surface-2" : ""}`;
                      }}
                    >
                      {({ selected }) => (
                        <div className="flex items-center space-x-3">
                          {getProviderIcon(model.provider)}
                          <div className="flex-1 min-w-0">
                            <span className={`block truncate font-medium ${disabled ? "text-subtle" : ""}`}>
                              {model.name || getFoundationModelName(model.foundation_model_id)}
                            </span>

                            {model.long_description && (
                              <div className="text-xs text-subtle mt-1">
                                {model.long_description}
                              </div>
                            )}
                          </div>
                          {selected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </div>
                      )}
                    </Listbox.Option>
                  );
                })}
              </Listbox.Options>
            </Transition>
          </div>
        </Listbox>
      </div>
      <EffortMenu />
    </div>
  );
}